import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { ApiError, api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { formatDateTime } from '../lib/format';
import { Badge, Button, Card, CardBody, Dialog, Field, Input, useToast } from './ui';
import { ChangeLoginEmailDialog } from './ChangeLoginEmailDialog';

/** Zugang einer Person, wie ihn die Zugangsverwaltung liefert. */
export interface AccessAccount {
  personId: string;
  firstName: string;
  lastName: string;
  email: string | null;
  userId: string | null;
  loginEmail: string | null;
  active: number | null;
  superadmin: number | null;
  lastLoginAt: string | null;
  invitationUrl: string | null;
  invitationExpiresAt: string | null;
  /** Geraete dieses Zugangs mit eingeschaltetem Push. */
  pushDevices: number;
}

/**
 * Zugang direkt an der Person: Status sehen, einladen, Link kopieren,
 * (de)aktivieren. Zugänge werden nur hier gepflegt, nicht in den
 * Einstellungen.
 */
export function PersonAccessCard({
  personId,
  personName,
  personEmail,
  personActive,
}: {
  personId: string;
  personName: string;
  personEmail: string | null;
  personActive: boolean;
}): JSX.Element | null {
  const { can, user } = useAuth();
  const mayManage = can('permissions', 'manage');
  const queryClient = useQueryClient();
  const toast = useToast();

  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [invitation, setInvitation] = useState<{ url: string; emailQueued: boolean } | null>(null);
  const [changeEmailOpen, setChangeEmailOpen] = useState(false);

  const accounts = useQuery({
    queryKey: ['operation-accounts'],
    queryFn: () => api.get<AccessAccount[]>('/api/operations/accounts'),
    enabled: mayManage,
  });
  const account = accounts.data?.find((entry) => entry.personId === personId) ?? null;
  const hasAccount = Boolean(account?.userId);

  const refresh = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: ['operation-accounts'] });
    await queryClient.invalidateQueries({ queryKey: ['person', personId] });
    await queryClient.invalidateQueries({ queryKey: ['persons'] });
    await queryClient.invalidateQueries({ queryKey: ['auth', 'view-as-targets'] });
  };

  const invite = useMutation({
    mutationFn: () =>
      api.post<{ invitationUrl: string; emailQueued: boolean }>('/api/operations/accounts/invite', {
        personId,
        email: email.trim(),
      }),
    onSuccess: async (result) => {
      await refresh();
      setInvitation({ url: result.invitationUrl, emailQueued: result.emailQueued });
      toast.show(result.emailQueued ? 'Einladung wurde versendet' : 'Einladungslink wurde erstellt');
    },
    onError: (err: unknown) =>
      toast.show(err instanceof ApiError ? err.message : 'Einladung konnte nicht angelegt werden', 'error'),
  });

  const toggle = useMutation({
    mutationFn: () => api.patch(`/api/operations/accounts/${account?.userId}`, { active: account?.active !== 1 }),
    onSuccess: async () => {
      await refresh();
      toast.show(account?.active === 1 ? 'Zugang deaktiviert' : 'Zugang aktiviert');
    },
    onError: (err: unknown) =>
      toast.show(err instanceof ApiError ? err.message : 'Zugang konnte nicht geändert werden', 'error'),
  });

  const copy = async (url: string): Promise<void> => {
    try {
      await navigator.clipboard.writeText(url);
      toast.show('Einladungslink kopiert');
    } catch {
      toast.show('Link konnte nicht kopiert werden', 'error');
    }
  };

  const openInvite = (): void => {
    setInvitation(null);
    setEmail(account?.loginEmail ?? personEmail ?? '');
    setOpen(true);
  };

  if (!mayManage) return null;

  const isSelf = account?.userId !== null && account?.userId === user?.userId;
  const neverLoggedIn = hasAccount && !account?.lastLoginAt;
  // Die Anmeldung eines Administrators darf nur ein Administrator umlenken.
  const mayChangeEmail = hasAccount && (account?.superadmin !== 1 || user?.isSuperadmin === true);

  return (
    <Card>
      <CardBody className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h2 className="font-semibold">Zugang</h2>
            <p className="text-sm text-slate-500">Anmeldung für die Vereinsplattform</p>
          </div>
          {!accounts.isLoading && personActive && (!hasAccount || (account?.active === 1 && neverLoggedIn)) && (
            <Button size="sm" onClick={openInvite}>
              {hasAccount ? 'Einladung erneut senden' : 'Zugang einladen'}
            </Button>
          )}
        </div>

        {accounts.isLoading ? (
          <p className="text-sm text-slate-500">Wird geladen …</p>
        ) : !hasAccount ? (
          <p className="text-sm text-slate-600">
            {personActive
              ? 'Noch kein Zugang. Mit einer Einladung legt die Person selbst ihr Passwort fest.'
              : 'Noch kein Zugang. Inaktive Personen können nicht eingeladen werden.'}
          </p>
        ) : (
          <div className="flex flex-wrap items-center justify-between gap-3 text-sm">
            <div className="min-w-0 space-y-0.5">
              <p className="flex flex-wrap items-center gap-2">
                <span className="font-medium text-slate-800">{account?.loginEmail}</span>
                {account?.active === 1 ? <Badge tone="success">Aktiv</Badge> : <Badge tone="neutral">Deaktiviert</Badge>}
                {account?.superadmin === 1 && <Badge tone="warning">Administrator</Badge>}
              </p>
              <p className="text-xs text-slate-500">
                Letzte Anmeldung: {account?.lastLoginAt ? formatDateTime(account.lastLoginAt) : 'noch nie'}
                {account?.invitationExpiresAt && ` · Einladung gültig bis ${formatDateTime(account.invitationExpiresAt)}`}
                {' · Push: '}{account?.pushDevices ? (account.pushDevices === 1 ? 'auf 1 Gerät' : `auf ${account.pushDevices} Geräten`) : 'aus'}
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              {mayChangeEmail && (
                <Button size="sm" variant="ghost" onClick={() => setChangeEmailOpen(true)}>
                  Adresse ändern
                </Button>
              )}
              {account?.invitationUrl && (
                <Button size="sm" variant="ghost" onClick={() => void copy(account.invitationUrl!)}>
                  Link kopieren
                </Button>
              )}
              {account?.superadmin !== 1 && !isSelf && (
                <Button
                  size="sm"
                  variant="secondary"
                  loading={toggle.isPending}
                  onClick={() => {
                    const text = account?.active === 1
                      ? `Zugang von ${personName} deaktivieren? Laufende Anmeldungen werden beendet.`
                      : `Zugang von ${personName} wieder aktivieren?`;
                    if (window.confirm(text)) toggle.mutate();
                  }}
                >
                  {account?.active === 1 ? 'Deaktivieren' : 'Aktivieren'}
                </Button>
              )}
            </div>
          </div>
        )}
      </CardBody>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        title={`Zugang für ${personName}`}
        footer={invitation ? (
          <>
            <Button variant="secondary" onClick={() => setOpen(false)}>Schließen</Button>
            <Button onClick={() => void copy(invitation.url)}>Link kopieren</Button>
          </>
        ) : (
          <>
            <Button variant="secondary" onClick={() => setOpen(false)}>Abbrechen</Button>
            <Button loading={invite.isPending} disabled={!/^\S+@\S+\.\S+$/.test(email.trim())} onClick={() => invite.mutate()}>
              Einladen
            </Button>
          </>
        )}
      >
        {invitation ? (
          <div className="space-y-3">
            <p className="rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
              {invitation.emailQueued
                ? 'Die Einladung wurde per E-Mail vorgemerkt. Der Link kann zusätzlich kopiert werden.'
                : 'Der E-Mail-Versand ist nicht eingerichtet. Kopiere diesen Link und sende ihn der Person direkt.'}
            </p>
            <Field label="Einladungslink" hint="Der Link ist 24 Stunden gültig.">
              {(id) => <Input id={id} value={invitation.url} readOnly onFocus={(event) => event.currentTarget.select()} />}
            </Field>
          </div>
        ) : (
          <div className="space-y-3">
            <Field
              label="E-Mail-Adresse"
              required
              hint={hasAccount ? 'Die Einladung geht an die Adresse des bestehenden Zugangs.' : 'Mit dieser Adresse meldet sich die Person künftig an.'}
            >
              {(id) => (
                <Input
                  id={id}
                  type="email"
                  value={email}
                  disabled={hasAccount}
                  onChange={(event) => setEmail(event.target.value)}
                  placeholder="name@verein.at"
                />
              )}
            </Field>
          </div>
        )}
      </Dialog>

      <ChangeLoginEmailDialog
        open={changeEmailOpen}
        onClose={() => setChangeEmailOpen(false)}
        userId={account?.userId ?? null}
        personName={personName}
        currentEmail={account?.loginEmail ?? null}
      />
    </Card>
  );
}
